import { PeripheralService } from "../PeripheralService";
import { PeripheralServiceConfig, PeripheralType } from "../types";
import { Peripheral } from "../Peripheral";
import { PrintApiClient } from "./PrintApiClient";
import { PrintApiPrinter, PrintApiServiceConfig } from "./types";

export abstract class PrintApiService<
  T extends Peripheral
> extends PeripheralService<T> {
  isSupported: boolean = true;
  private printApiClient: PrintApiClient;

  // Set to true on the first successful getAllPrinters() call; never reset.
  // Used by PeripheralServicePrinterManager to gate local printer sync.
  public discoveryComplete = false;
  get locationIdentifier(): string { return this.printApiClient.locationIdentifier; }

  constructor(
    peripheralType: PeripheralType,
    serviceName: string,
    config: Partial<PeripheralServiceConfig> & PrintApiServiceConfig
  ) {
    super(
      peripheralType,
      {
        autoConnect: false,
        autoEnable: true,
        name: serviceName,
      },
      config
    );

    // Initialize the singleton PrintApiClient with required baseUrl and apiToken
    this.printApiClient = PrintApiClient.getInstance({
      baseUrl: config.baseUrl,
      locationIdentifier: config.locationIdentifier,
    });
  }

  protected async availableDevices(): Promise<T[]> {
    try {
      const allPrinters = await this.printApiClient.getAllPrinters();
      this.discoveryComplete = true;
      return allPrinters
        .filter((printer) => this.shouldIncludePrinter(printer))
        .map((printer) => this.createPrinter(printer));
    } catch (error) {
      console.error("Failed to fetch printers from API:", error);
      // Return empty array on error to prevent service from failing completely
      return [];
    }
  }

  // Abstract method for each service to implement printer creation
  protected abstract createPrinter(device: PrintApiPrinter): T;

  // Override this to filter printers by type (receipt vs label)
  protected abstract shouldIncludePrinter(device: PrintApiPrinter): boolean;
}
